import React, { useState, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@/components/CustomDropdown';
import ModalContainer from '@/components/modals/ModalContainer';
import CommonImage from '@/components/CommonImage';
import {
	useAddDashboardCardMutation,
	useGetFilterListQuery,
	useUpdateDashboardCardMutation
} from '@/features/task-configuration/api/taskConfigurationApi';
import { DASHBOARD_SECTIONS, HEADER_STYLE } from '@/features/task-configuration/constants';
import { toastError, toastSuccess } from '@/components/Toast';

// Create a custom design function that returns JSX
const customDropdownDesign = (item) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			gap: '8px',
			padding: '4px 8px',
			backgroundColor: item.bgColor,
			borderRadius: '4px',
			width: '100%'
		}}
	>
		<CommonImage src={`${item.icon}`} alt="" style={{ width: 20, height: 20 }} />
		<span style={{ color: item.textColor, fontWeight: 500 }}>{item.value}</span>
	</div>
);

const INITIAL_STATE = {
	cardTitle: '',
	taskList: [],
	headerSectionStyle: null,
	section: null,
	description: '',
	serial: ''
};

const CreateAndEditDashboard = ({ onClose, selectedCardToEdit, selectedUserType }) => {
	const [formData, setFormData] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);
	const [addDashboardCard, { isLoading: cardCreating }] = useAddDashboardCardMutation();
	const [updateDashboardCard, { isLoading: cardUpdating }] = useUpdateDashboardCardMutation();
	const { data: filterList } = useGetFilterListQuery();

	// Populate form when editing
	useEffect(() => {
		if (selectedCardToEdit) {
			let headerStyle = null;
			if (selectedCardToEdit.titleStyle) {
				try {
					const parsedStyle = JSON.parse(selectedCardToEdit.titleStyle);

					const foundStyle = HEADER_STYLE.find((style) => style.id === parsedStyle?.id);
					if (foundStyle) {
						headerStyle = foundStyle;
					} else if (
						parsedStyle &&
						parsedStyle.bgColor &&
						parsedStyle.textColor &&
						parsedStyle.icon
					) {
						headerStyle = parsedStyle;
					}
				} catch {
					headerStyle = HEADER_STYLE.find((style) => style.id === selectedCardToEdit.titleStyle);
				}
			}

			// Map section from API format to dropdown format
			const section = selectedCardToEdit.section
				? {
						id: selectedCardToEdit.section.constant,
						value: selectedCardToEdit.section.name,
						name: selectedCardToEdit.section.name
					}
				: null;

			setFormData({
				cardTitle: selectedCardToEdit.title || '',
				taskList:
					selectedCardToEdit.filters?.map((filter) => ({
						id: filter.id,
						value: filter.title,
						label: filter.title
					})) || [],
				headerSectionStyle: headerStyle,
				section: section,
				description: selectedCardToEdit.details || '',
				serial: selectedCardToEdit.serial || ''
			});
		} else {
			setFormData(INITIAL_STATE);
		}
	}, [selectedCardToEdit]);

	const handleChange = (value, name) => {
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData?.section?.id) {
			newErrors.section = 'Section is required';
		}
		setErrors(newErrors);

		return Object.keys(newErrors)?.length === 0;
	};

	const getPayload = () => {
		return {
			title: formData.cardTitle,
			filterIds: formData.taskList?.map((item) => item.id),
			section: formData.section?.id,
			titleStyle: formData?.headerSectionStyle ? JSON.stringify(formData.headerSectionStyle) : null,
			details: formData.description,
			userType: selectedUserType,
			serial: formData.serial
		};
	};

	const handleSubmit = () => {
		if (!validateForm()) {
			return;
		}

		if (selectedCardToEdit) {
			updateDashboardCard({
				id: selectedCardToEdit.id,
				body: getPayload()
			})
				.unwrap()
				.then(() => {
					toastSuccess('Dashboard card updated successfully');
					onClose();
				})
				.catch((err) => {
					toastError(err?.data?.message);
				});
		} else {
			addDashboardCard({ body: getPayload() })
				.unwrap()
				.then(() => {
					toastSuccess('Dashboard card added successfully');
					onClose();
				})
				.catch((err) => {
					toastError(err?.data?.message);
				});
		}
	};

	return (
		<ModalContainer className="create-dashboard-modal">
			<div className="popup-wrapper">
				<div className="common-popup">
					<div className="common-popup-header d-flex justify-content-between">
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">
								{selectedCardToEdit ? 'Edit dashboard' : 'Create dashboard'}
							</h3>
						</div>
						<div className="close-btn" onClick={onClose}>
							<CommonImage src="/icons/close.svg" alt="close" width={24} height={24} />
						</div>
					</div>
					<div className="common-popup-body">
						<div className="row">
							<div className="col-md-12 pr-2">
								<div className="form-group">
									<label htmlFor="collection">Card title</label>
									<input
										value={formData?.cardTitle}
										onChange={(e) => handleChange(e.target.value, 'cardTitle')}
										type="text"
										placeholder="Write the card name"
									/>
								</div>
							</div>
							<div className="col-md-12 pr-2">
								<div className="form-group no-shadow-react-select">
									<label htmlFor="collection">List item</label>
									<CreatableSelect
										isMulti
										value={formData?.taskList}
										onChange={(value) => handleChange(value, 'taskList')}
										options={filterList?.map((item) => ({
											...item,
											id: item?.id,
											value: item?.title,
											label: item?.title
										}))}
										isValidNewOption={() => false}
										isClearable
									/>
								</div>
							</div>
							<div className="col-md-12">
								<div className="form-group">
									<label htmlFor="collection">Card header section styling</label>
									<CustomDropdown
										title="Select card header section styling"
										onItemClick={(value) => handleChange(value, 'headerSectionStyle')}
										selectedItem={formData.headerSectionStyle?.value}
										items={HEADER_STYLE}
										customDesign={customDropdownDesign}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="collection">Section*</label>
									<CustomDropdown
										title="Select section"
										onItemClick={(value) => handleChange(value, 'section')}
										selectedItem={formData.section?.value}
										items={DASHBOARD_SECTIONS}
									/>
									{errors?.section && <p className="error">{errors?.section}</p>}
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="collection">Card serial</label>
									<input
										type="number"
										placeholder="Card serial"
										value={formData.serial}
										onChange={(e) => handleChange(e.target.value, 'serial')}
										onWheel={(e) => e.target.blur()}
									/>
								</div>
							</div>
							<div className="col-md-12">
								<div className="form-group">
									<label htmlFor="collection">Description</label>
									<textarea
										name="description"
										id="Description"
										placeholder="Description"
										value={formData.description}
										onChange={(e) => handleChange(e.target.value, 'description')}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="modal-footer">
						<div className="footer-buttons text-right">
							<button className="button outline size32 ml-2" onClick={onClose}>
								Cancel
							</button>
							<button
								className="button size32 ml-2"
								disabled={cardCreating || cardUpdating}
								onClick={handleSubmit}
							>
								{cardCreating || cardUpdating ? 'Submiting...' : 'Submit'}
							</button>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default CreateAndEditDashboard;
